import React, {ReactNode, useMemo} from "react";

export type CellRendererArgs<T> = {
	row: T;
	rowId: string;
	columnId: string;
	value: any;
	onChange: (next: any) => void;
	isDirty: boolean;
};

export type ColumnDef<T> = {
	id: string;
	header: ReactNode;
	className?: string;
	getValue?: (row: T) => any;
	cell: (args: CellRendererArgs<T>) => ReactNode;
	size?: string; // css grid width, e.g.: "1fr", "minmax(10em, 2fr)", ...
};

export type EditableTableProps<T> = {
	rows: T[],
	getRowId: (row: T) => string,
	columns: ColumnDef<T>[],
	cellEdits?: Record<string, Record<string, any>>,
	onValueChange: (rowId: string, columnId: string, next: any) => void,
	getRowClassName?: (row: T, rowId: string, isRowDirty: boolean) => string,
	headerExtra?: ReactNode,
	footerExtra?: ReactNode,
	maxHeight?: string
};

export function ZeeroDataTable<T>({
									  rows,
									  getRowId,
									  columns,
									  cellEdits,
									  onValueChange,
									  getRowClassName,
									  headerExtra,
									  footerExtra,
									  maxHeight
								  }: EditableTableProps<T>) {

	const template = useMemo(() => columns.map(c => c.size ?? '1fr').join(' '), [columns]);

	const readCurrent = (row: T, rowId: string, col: ColumnDef<T>) => {
		const overlay = cellEdits?.[rowId]?.[col.id];
		if (overlay !== undefined) return overlay;
		return col.getValue ? col.getValue(row) : (row as any)[col.id];
	};

	const isRowDirty = (row: T, rowId: string) =>
			// if no cellEdits provided, nothing is dirty
			!cellEdits ? false :
					columns.some((col) => {
						const original = col.getValue ? col.getValue(row) : (row as any)[col.id];
						const current = readCurrent(row, rowId, col);
						return current !== original;
					});

	return (
			<div className="zeero-data-table" style={{['--zeero-col-template' as any]: template}}>
				<div className="zeero-data-header">
					{columns.map((column) => (
							<div key={column.id} className={`zeero-data-header-entry ${column.className ?? ""}`}>
								{column.header}
							</div>
					))}
					{headerExtra ? <div className="zeero-data-header">{headerExtra}</div> : null}
				</div>

				<div className="zeero-data-body" style={{maxHeight: maxHeight ?? "unset"}}>
					{rows.map((row) => {
						const rowId = getRowId(row);
						const dirty = isRowDirty(row, rowId);
						const rowClass = `zeero-data-row${dirty ? " updated" : ""} ${getRowClassName?.(row, rowId, dirty) ?? ""}`;

						return (
								<div key={rowId} className={rowClass}>
									{columns.map((col) => {
										const current = readCurrent(row, rowId, col);
										const original = col.getValue ? col.getValue(row) : (row as any)[col.id];
										const handleChange = (next: any) => onValueChange(rowId, col.id, next);

										return (
												<div key={col.id}
													 className={`zeero-data-row-entry ${col.className ?? ""}`}>
													{col.cell({
														row,
														rowId,
														columnId: col.id,
														value: current,
														onChange: handleChange,
														isDirty: !!cellEdits && current !== original,
													})}
												</div>
										);
									})}
								</div>
						);
					})}

				</div>

				{footerExtra ? <div className="zeero-data-footer">{footerExtra}</div> : null}
			</div>
	);
}
